import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import { Grid, Stack, Typography } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { APP_ICONS } from "../../../assets";
import AppLoader from "../../../components/AppLoader";
import PaperContainer from "../../../components/Containers/PaperContainer";
import ServiceBlockConfirmDialog from "../../../components/Dialog/ServiceBlockConfirmDialog";
import AddEditServiceSubCategoryDialog from "../../../components/Dialogs/AddEditServiceSubCategory";
import Label from "../../../components/Label";
import GlobalTable from "../../../components/Tables/GlobalTable";
import TextInput from "../../../components/TextInputs";
import globalStyle from "../../../globalStyle";
import useDebounce from "../../../hooks/useDebounce";
import { setActiveTab } from "../../../lib/slices/activeTabSlice";
import { setSearchKeys } from "../../../lib/slices/searchKeysSlice";
import { getServiceCategoryDetail } from "../../../services/serviceCategoryApiFn";
import {
  getAllServiceSubCategoryById,
  updateServiceSubCategory,
} from "../../../services/serviceSubCategoryApiFn";
import { handleOpenSnackBar } from "../../../utils";

const headerData = [
  "createdAt",
  "imageName",
  "icon",
  "order",
  "percentage",
  "description",
  "status",
  "actions",
];

const tableRows = [
  "created_date",
  "service_sub_category",
  "active_Icon",
  "serialNo",
  "percentage",
  "category_description",
  "status",
  "actions",
];

const CategoryDetails = () => {
  const openDialog = useRef(null);
  const openConfirmDialog = useRef(null);
  const dispatch = useDispatch();
  const { t } = useTranslation(["catalogue", "common"]);
  const [isLoading, setIsLoading] = useState(false);
  const [categoryDetail, setCategoryDetail] = useState(null);
  const [allSubCategory, setAllSubCategory] = useState([]);
  const activeTab = useSelector((state) => state.activeTab.tabs);
  const serviceCategoryId = useSelector(
    (state) => state.detailIdReducer.detailIds.serviceCategoryId
  );
  const searchKeys = useSelector((state) => state.searchKeysReducer.searchKeys);
  const debouncedSearchTerm = useDebounce(searchKeys.subServiceName, 1000);
  const currentLanguage = useSelector(
    (state) => state.languageReducer.currentLanguage
  );

  useEffect(() => {
    if (serviceCategoryId) {
      fetchServiceCategoryDetail();
    }
  }, [serviceCategoryId]);

  useEffect(() => {
    if (serviceCategoryId) {
      fetchAllServiceSubCategory(debouncedSearchTerm);
    }
  }, [serviceCategoryId, debouncedSearchTerm]);

  const fetchServiceCategoryDetail = async () => {
    try {
      setIsLoading(true);
      const response = await getServiceCategoryDetail(serviceCategoryId);
      if (response.data.status === "success") {
        let data = response?.data.data;
        setCategoryDetail(data);
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAllServiceSubCategory = async (name) => {
    try {
      setIsLoading(true);
      const response = await getAllServiceSubCategoryById(
        serviceCategoryId,
        name
      );
      if (response.data.status === "success") {
        let data = response?.data.data;
        setAllSubCategory(data);
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    dispatch(setActiveTab({ ...activeTab, catalogue: "service_category" }));
  };

  const handleEdit = (params) => {
    openDialog.current.openDialog({
      type: "edit",
      data: params.row,
      getData: fetchAllServiceSubCategory,
    });
  };

  const handleOpenConfirmDialog = (event, row, index) => {
    openConfirmDialog.current.openDialog({ event, row, index });
  };

  const handleChangeStatus = async (params) => {
    try {
      let payload = {
        status: params.event,
      };
      setIsLoading(true);
      const response = await updateServiceSubCategory(params.row._id, payload);
      if (response.data.status) {
        openConfirmDialog.current.closeDialog();
        fetchAllServiceSubCategory(debouncedSearchTerm);
        handleOpenSnackBar("success", response.data.message);
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <AppLoader isVisible={isLoading} />
      <PaperContainer
        title={t("serviceDetail")}
        subtitle={t("viewCategoryDetails")}
        showBackButton={true}
        handleBack={handleBack}
        onBack={handleBack}
      >
        <Grid container spacing={2} alignItems={"center"}>
          <Grid
            item
            xs={12}
            md={3}
            display={"flex"}
            alignItems={"center"}
            justifyContent={"center"}
          >
            <img src={categoryDetail?.image} height={200} width={"100%"} />
          </Grid>
          <Grid item xs={12} md={9}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Label
                  required
                  labelText={currentLanguage === "en" ? t("categoryNameInEnglish") : t("categoryNameInRomanian")}
                  htmlFor={"catrgory-name"}
                />
                <TextInput
                  id={"catrgory-name"}
                  // placeholder={t("enterName")}
                  value={categoryDetail?.name[currentLanguage]}
                  readonly
                />
              </Grid>
              {/* <Grid item xs={12} md={6}>
                <Label labelText="Profit%" />
                <TextInput styleProps={globalStyle.textField} />
              </Grid> */}
              <Grid item xs={12}>
                <Stack direction={"row"} justifyContent={"space-between"}>
                  <Label
                    required
                    labelText={currentLanguage === "en" ? t("categoryDescriptionInEnglish") : t("categoryDescriptionInRomanian")}
                    htmlFor={"category-description"}
                  />
                  <Typography variant="caption">
                    {categoryDetail?.description[currentLanguage]?.length}/255
                  </Typography>
                </Stack>
                <TextInput
                  required
                  // placeholder={`${t("enter")} ${t("description")}`}
                  value={categoryDetail?.description[currentLanguage]}
                  readonly
                  multiline
                  rows={3}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Stack
          p={2}
          my={3}
          borderRadius={2}
          direction={{ sm: "row" }}
          alignItems={"center"}
          justifyContent={"space-between"}
          bgcolor={"primary.light"}
        >
          <TextInput
            styleProps={{
              ...globalStyle.textField,
              width: { xs: "100%", sm: 300 },
            }}
            placeholder={t("searchByName", { ns: "common" })}
            InputStartIcon={
              <img
                src={APP_ICONS.searchGreyIcon}
                alt="search-icon"
                width={25}
              />
            }
            InputEndIcon={
              searchKeys.subServiceName && (
                <CancelOutlinedIcon
                  fontSize="small"
                  sx={{ cursor: "pointer" }}
                  onClick={() => {
                    dispatch(
                      setSearchKeys({
                        ...searchKeys,
                        subServiceName: "",
                      })
                    );
                  }}
                />
              )
            }
            value={searchKeys.subServiceName}
            onChange={(e) => {
              dispatch(
                setSearchKeys({
                  ...searchKeys,
                  subServiceName: e.target.value,
                })
              );
            }}
          />
          {/* <GlobalButton
            variant={"contained"}
            btnText={"addNew"}
            handleClickBtn={() =>
              openDialog.current.openDialog({
                type: "add",
                data: {},
                getData: fetchAllServiceSubCategory,
              })
            }
            styleProps={{
              height: 40,
              width: "auto",
              fontWeight: 400,
              fontSize: "0.875rem",
            }}
          /> */}
        </Stack>
        <GlobalTable
          tableHeader={headerData}
          displayRows={tableRows}
          tableData={allSubCategory}
          showActions={true}
          showEdit={true}
          handleEdit={handleEdit}
          handleChangeStatus={handleOpenConfirmDialog}
        />
      </PaperContainer>
      <AddEditServiceSubCategoryDialog ref={openDialog} />
      <ServiceBlockConfirmDialog
        ref={openConfirmDialog}
        handleConfirm={handleChangeStatus}
      />
    </>
  );
};

export default CategoryDetails;
